import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Bell,
  Plus,
  RefreshCw,
  CheckCircle2,
  X,
  Edit,
  Eye,
  EyeOff,
  Trash2,
  AlertTriangle,
  Calendar,
  Tag,
  Send,
  Clock
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const ANNOUNCEMENT_CATEGORIES = [
  { value: 'general', label: 'General', color: 'blue' },
  { value: 'event', label: 'Event', color: 'purple' },
  { value: 'urgent', label: 'Urgent', color: 'red' },
  { value: 'reminder', label: 'Reminder', color: 'amber' },
  { value: 'celebration', label: 'Celebration', color: 'emerald' }
];

const ANNOUNCEMENT_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'slate' },
  { value: 'published', label: 'Published', color: 'emerald' },
  { value: 'unpublished', label: 'Unpublished', color: 'amber' }
];

const AnnouncementsSection = ({ adminUser }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    text: '',
    category: 'general',
    status: 'draft',
    publish_date: '',
    expiry_date: '',
    priority: 0
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/announcements/enhanced`, {
        params: { include_all: true }
      });
      // Filter out archived announcements
      const activeAnnouncements = response.data.filter(a => a.status !== 'archived');
      setAnnouncements(activeAnnouncements);
      
      // Calculate stats
      const published = activeAnnouncements.filter(a => a.status === 'published').length;
      const draft = activeAnnouncements.filter(a => a.status === 'draft').length;
      setStats({ total: activeAnnouncements.length, published, draft });
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async () => {
    if (!formData.title || !formData.text) return;
    
    try {
      if (editingId) {
        await axios.put(`${API}/announcements/enhanced/${editingId}`, formData);
      } else {
        await axios.post(`${API}/announcements/enhanced`, {
          ...formData,
          created_by: adminUser?.id || ''
        });
      }
      
      setFormData({
        title: '',
        text: '',
        category: 'general',
        status: 'draft',
        publish_date: '',
        expiry_date: '',
        priority: 0
      });
      setShowForm(false);
      setEditingId(null);
      loadData();
    } catch (error) {
      console.error('Error saving announcement:', error);
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      title: announcement.title,
      text: announcement.text,
      category: announcement.category || 'general',
      status: announcement.status || 'draft',
      publish_date: announcement.publish_date || '',
      expiry_date: announcement.expiry_date || '',
      priority: announcement.priority || 0
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const announcement = announcements.find(a => a.id === id);
      await axios.put(`${API}/announcements/enhanced/${id}`, {
        ...announcement,
        status: newStatus
      });
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePermanentDelete = async (id) => {
    try {
      await axios.delete(`${API}/announcements/enhanced/${id}/permanent`);
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Failed to delete announcement');
    }
  };

  const filteredAnnouncements = filterStatus 
    ? announcements.filter(a => a.status === filterStatus)
    : announcements;

  const getCategoryBadge = (category) => {
    const cat = ANNOUNCEMENT_CATEGORIES.find(c => c.value === category) || ANNOUNCEMENT_CATEGORIES[0];
    const colorMap = {
      blue: 'bg-blue-100 text-blue-700',
      purple: 'bg-purple-100 text-purple-700',
      red: 'bg-red-100 text-red-700',
      amber: 'bg-amber-100 text-amber-700',
      emerald: 'bg-emerald-100 text-emerald-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorMap[cat.color]}`}>
        {cat.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const s = ANNOUNCEMENT_STATUSES.find(st => st.value === status) || ANNOUNCEMENT_STATUSES[0];
    const colorMap = {
      slate: 'bg-slate-100 text-slate-700',
      emerald: 'bg-emerald-100 text-emerald-700',
      amber: 'bg-amber-100 text-amber-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorMap[s.color]}`}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="announcements-section">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Announcements</h2>
              <p className="text-sm text-slate-500">Manage church announcements and notices</p>
            </div>
          </div>
          <div className="flex gap-2">
            <motion.button
              onClick={loadData}
              className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </motion.button>
            <motion.button
              onClick={() => {
                setEditingId(null);
                setFormData({
                  title: '',
                  text: '',
                  category: 'general',
                  status: 'draft',
                  publish_date: '',
                  expiry_date: '',
                  priority: 0
                });
                setShowForm(true);
              }}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium hover:opacity-90 transition-all flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-4 h-4" />
              New Announcement
            </motion.button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-slate-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
            <p className="text-sm text-slate-500">Total</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.published}</p>
            <p className="text-sm text-emerald-600">Published</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.draft}</p>
            <p className="text-sm text-amber-600">Drafts</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
        <div className="flex gap-2 overflow-x-auto">
          <button
            onClick={() => setFilterStatus('')}
            className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
              !filterStatus ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>
          {ANNOUNCEMENT_STATUSES.map(status => (
            <button
              key={status.value}
              onClick={() => setFilterStatus(status.value)}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                filterStatus === status.value
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-full bg-red-100">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Permanently Delete?</h3>
              </div>
              <p className="text-slate-600 mb-6">
                This action cannot be undone. The announcement will be permanently removed from the system.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handlePermanentDelete(deleteConfirm)}
                  className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Permanently
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">
                  {editingId ? 'Edit Announcement' : 'New Announcement'}
                </h3>
                <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                  <input
                    type="text"
                    placeholder="Announcement title"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 outline-none"
                    value={formData.title}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Content *</label>
                  <textarea
                    placeholder="Announcement content..."
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 outline-none"
                    rows={4}
                    value={formData.text}
                    onChange={e => setFormData(prev => ({ ...prev, text: e.target.value }))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                    <select
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 outline-none"
                      value={formData.category}
                      onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    >
                      {ANNOUNCEMENT_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                    <select
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 outline-none"
                      value={formData.status}
                      onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    >
                      {ANNOUNCEMENT_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Publish Date</label>
                    <input
                      type="date"
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 outline-none"
                      value={formData.publish_date}
                      onChange={e => setFormData(prev => ({ ...prev, publish_date: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Expiry Date</label>
                    <input
                      type="date"
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 outline-none"
                      value={formData.expiry_date}
                      onChange={e => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <select
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-blue-400 outline-none"
                    value={formData.priority}
                    onChange={e => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                  >
                    <option value={0}>Normal</option>
                    <option value={1}>High</option>
                    <option value={2}>Urgent</option>
                  </select>
                </div>

                <motion.button
                  onClick={handleSubmit}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  {editingId ? 'Update Announcement' : 'Create Announcement'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Announcements List */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Loading announcements...</p>
        </div>
      ) : filteredAnnouncements.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
          <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-700 mb-2">No Announcements</h3>
          <p className="text-slate-500">Create your first announcement to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAnnouncements.map((announcement, index) => (
            <motion.div
              key={announcement.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getCategoryBadge(announcement.category)}
                    {getStatusBadge(announcement.status)}
                    {announcement.priority > 0 && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {announcement.priority === 2 ? 'Urgent' : 'High Priority'}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{announcement.title}</h3>
                  <p className="text-slate-600 text-sm line-clamp-2">{announcement.text}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    {announcement.publish_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {announcement.publish_date}
                      </span>
                    )}
                    {announcement.created_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(announcement.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {announcement.status === 'draft' && (
                    <motion.button
                      onClick={() => handleStatusChange(announcement.id, 'published')}
                      className="p-2 rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Publish"
                    >
                      <Send className="w-4 h-4" />
                    </motion.button>
                  )}
                  {announcement.status === 'published' && (
                    <motion.button
                      onClick={() => handleStatusChange(announcement.id, 'unpublished')}
                      className="p-2 rounded-lg bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Unpublish"
                    >
                      <EyeOff className="w-4 h-4" />
                    </motion.button>
                  )}
                  {announcement.status === 'unpublished' && (
                    <motion.button
                      onClick={() => handleStatusChange(announcement.id, 'published')}
                      className="p-2 rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Publish"
                    >
                      <Eye className="w-4 h-4" />
                    </motion.button>
                  )}
                  <motion.button
                    onClick={() => handleEdit(announcement)}
                    className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </motion.button>
                  <motion.button
                    onClick={() => setDeleteConfirm(announcement.id)}
                    className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Delete Permanently"
                  >
                    <Trash2 className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsSection;
